// ONB2 (docs/build-briefs/ONB2-first-day-setup-and-go-live.md, S4; QA 2026-10-11 item 13): the small pure rules behind
// four menu entry fixes. No DOM, no fetch: test/onb2-menu-entry-model.test.ts proves them.

import { formatCents } from "./money.ts";

/**
 * The price a per-ounce beer or wine tile shows. A pint rung at 16 oz and $0.40 an ounce is $6.40, so that is what the
 * tile says, not "$0.40/oz". One pour size shows its exact price; several show "From" the smallest. Cents are rounded
 * half up on whole hundredths of an ounce, the same rule the server quote uses. Returns null when the product has no
 * usable pour size (it cannot be rung either), so the caller keeps its old label.
 */
export function servingPriceLabel(
  perOunceCents: number,
  pourOunceOptions: readonly (string | number)[] | null,
): { text: string; variable: boolean; cents: number } | null {
  if (!Number.isInteger(perOunceCents) || perOunceCents < 0) return null;
  const hundredths: number[] = [];
  for (const option of pourOunceOptions ?? []) {
    const ounces = typeof option === "number" ? option : Number(option);
    if (!Number.isFinite(ounces) || ounces <= 0) continue;
    hundredths.push(Math.round(ounces * 100));
  }
  if (hundredths.length === 0) return null;
  const priceOf = (h: number): number => Math.floor((h * perOunceCents + 50) / 100);
  const smallest = priceOf(Math.min(...hundredths));
  const dollars = formatCents(smallest);
  return hundredths.length === 1
    ? { text: dollars, variable: false, cents: smallest }
    : { text: `From ${dollars}`, variable: true, cents: smallest };
}

/** "tier_1" is a stored key, not a name. A person reads "Tier 1". Any other key reads with its words capitalised. */
export function shelfTierKeyLabel(key: string): string {
  const words = key.split("_").filter((word) => word.length > 0);
  if (words.length === 0) return key;
  return words.map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`).join(" ");
}

/** TIERLABEL1: the label column of the Shelf tiers rows reads by position ("Tier 5" for the fifth row), never by the tier's own name or key. */
export function shelfTierPositionLabel(index: number): string {
  return `Tier ${index + 1}`;
}

/** A blank Extra charge means no extra charge. Anything else is left for the exact parser to accept or refuse by name. */
export function extraChargeInput(raw: string): string {
  return raw.trim() === "" ? "0" : raw;
}
